import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type InWorldSheetProps = {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Wider sheet for office tools */
  wide?: boolean;
  className?: string;
};

/**
 * Right-side sheet over the live HQ map — keeps the world visible (not a 90vh modal).
 * Escape / backdrop click closes when onClose is provided.
 */
export function InWorldSheet({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  wide = false,
  className,
}: InWorldSheetProps) {
  if (!open) return null;

  return (
    <div className="absolute inset-0 z-[75] flex justify-end">
      <button
        type="button"
        aria-label="Close panel"
        className="absolute inset-0 cursor-default border-0 bg-[rgba(0,24,71,0.35)] backdrop-blur-[2px]"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          "relative z-10 flex h-full w-full flex-col border-l border-border bg-background shadow-[-8px_0_24px_rgba(0,24,71,0.18)]",
          wide ? "max-w-[420px]" : "max-w-[380px]",
          className,
        )}
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0">
            <h2 className="text-base font-bold tracking-tight text-foreground">{title}</h2>
            {subtitle ? <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p> : null}
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={onClose} className="shrink-0">
            Close
          </Button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">{children}</div>
        {footer ? (
          <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-border px-4 py-3">
            {footer}
          </footer>
        ) : null}
      </aside>
    </div>
  );
}
